import { Box } from '@mui/material';
import { alpha } from '@mui/material/styles';
import React, { useCallback, useMemo, useState } from 'react';

import { CAMPAIGN_ITEMS_PER_PAGE_WHEN_SCROLLED_INFINITELY } from '../../../constants/campaigns';
import { useCurrency } from '../../../contexts/CurrencyProvider';
import useDebouncedCallback from '../../../hooks/useDebouncedCallback';
import useInfiniteScroll from '../../../hooks/useInfiniteScroll';
import { useCampaignsInfiniteScroll } from '../../../service/InventoryService';
import { ICampaign } from '../../../types/campaigns';
import { isCampaignEligibleToOrder } from '../../../utils/campaigns';
import { isSameFC } from '../../../utils/inventories';
import { AddSection } from './AddSection';
import { CampaignListItem } from './CampaignListItem';
import { SearchInput } from './SearchInput';

interface IProps {
  onClose: () => void;
  onSelect: (campaign: ICampaign) => void;
}

export const AddCampaign: React.FC<IProps> = ({ onClose, onSelect }) => {
  const [input, setInput] = useState('');
  const [query, setQuery] = useState(input);
  const { getCampaignTotal } = useCurrency();
  const containerRef = React.useRef(null);

  const {
    data: campaignData,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useCampaignsInfiniteScroll({
    page_size: CAMPAIGN_ITEMS_PER_PAGE_WHEN_SCROLLED_INFINITELY,
    search_query: query,
  });

  const campaignsList = useMemo<ICampaign[]>(() => (campaignData?.pages.flat() as ICampaign[]) || [], [campaignData]);

  const handleScroll = useInfiniteScroll({
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    threshold: 0.75,
  });

  const debouncedSetQuery = useDebouncedCallback((v: string) => {
    setQuery(v);
  }, 500);

  const onChange = useCallback(
    (v: string) => {
      setInput(v);
      debouncedSetQuery(v);
    },
    [debouncedSetQuery],
  );
  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleCampaignSelect = useCallback(
    (campaign: ICampaign) => {
      onSelect(campaign);
      onClose();
    },
    [onSelect, onClose],
  );

  const renderCampaignItem = useCallback(
    (box: ICampaign) => {
      const { box_id: boxId, items } = box;
      const isBoxDisabled = !isSameFC(items) || !isCampaignEligibleToOrder(items);
      const total = getCampaignTotal(box);

      return (
        <Box
          key={boxId}
          sx={{
            borderRadius: 1,
            cursor: 'pointer',
            '&:hover': { backgroundColor: (t) => alpha(t.palette.primary.main, 0.1) },
          }}
        >
          <CampaignListItem total={total} campaign={box} onClick={handleCampaignSelect} isDisabled={isBoxDisabled} />
        </Box>
      );
    },
    [getCampaignTotal, handleCampaignSelect],
  );

  return (
    <AddSection title="Add Campaigns" onClose={handleCancel}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        }}
      >
        <SearchInput
          value={input}
          onChange={(v) => onChange(v as string)}
          isLoading={isFetching}
          placeholderText="campaigns"
        />
        <Box
          sx={{
            marginY: 2,
            flex: '1 1 auto',
            display: 'grid',
            gridTemplateRows: 'repeat(1fr)',
            gap: 1,
            overflowY: 'auto',
          }}
          onScroll={handleScroll}
          ref={containerRef}
        >
          {campaignsList.map(renderCampaignItem)}
        </Box>
      </Box>
    </AddSection>
  );
};
